import { createPortal } from 'react-dom';

/**
 * UniversalModal - A comprehensive modal component that combines all modal features
 *
 * Features:
 * - Optional portal rendering for high z-index scenarios
 * - Flexible sizing (preset sizes or custom maxWidth)
 * - Subtitle support
 * - Custom footer support
 * - Processing state to prevent closing
 * - Theme-aware styling
 * - Scrollable content area
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {function} onClose - Callback when modal should close
 * @param {string|ReactNode} title - Modal title (string or custom element)
 * @param {string} subtitle - Optional subtitle text
 * @param {ReactNode} children - Modal content
 * @param {ReactNode} footer - Optional custom footer content
 * @param {string} size - Preset size: 'sm', 'md', 'lg', 'xl' (ignored if maxWidth is set)
 * @param {string} maxWidth - Custom max-width class (overrides size)
 * @param {number} zIndex - Custom z-index (default: 50)
 * @param {boolean} usePortal - Whether to render in a portal at body level (default: false)
 * @param {boolean} isProcessing - Whether to disable closing (default: false)
 * @param {string} className - Additional classes for the modal container
 */
export default function UniversalModal({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  maxWidth,
  zIndex = 50,
  usePortal = false,
  isProcessing = false,
  className = ''
}) {
  if (!isOpen) return null;

  // Size presets
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-2xl',
    lg: 'max-w-4xl',
    xl: 'max-w-6xl'
  };

  // Use custom maxWidth if provided, otherwise use size preset
  const widthClass = maxWidth || sizeClasses[size] || sizeClasses.md;

  const handleBackdropClick = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const modalContent = (
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ zIndex }}
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Modal Container */}
      <div
        className={`relative bg-gray-800 rounded-lg shadow-xl border border-gray-700 w-full ${widthClass} max-h-[90vh] flex flex-col ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-700 flex-shrink-0">
          <div className="flex-1 min-w-0">
            {typeof title === 'string' ? (
              <>
                <h2 className="text-xl font-bold text-white">{title}</h2>
                {subtitle && <p className="text-gray-400 text-sm mt-1">{subtitle}</p>}
              </>
            ) : (
              <div className="text-xl font-bold text-white">{title}</div>
            )}
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="ml-4 w-8 h-8 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-white transition-all duration-200 flex items-center justify-center flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body - Scrollable Content Area */}
        <div className="overflow-y-auto flex-1 min-h-0">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="border-t border-gray-700 flex-shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>
  );

  // Render in portal if requested (for high z-index scenarios like dropdowns)
  if (usePortal) {
    return createPortal(modalContent, document.body);
  }

  return modalContent;
}
